// ═══════════════════════════════════════════════════════════════
// ClearView CRM Auth Service — Supabase Authentication
// ═══════════════════════════════════════════════════════════════
import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

export const authService = {
  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      debugLog.error("authService.signIn", error);
      throw error;
    }

    // Fetch the user's profile for role info
    const profile = await this.getProfile(data.user.id);
    debugLog.action("User signed in", { email, role: profile?.role });
    return { user: data.user, profile };
  },

  /**
   * Sign out
   */
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      debugLog.error("authService.signOut", error);
      throw error;
    }
    debugLog.action("User signed out");
  },

  /**
   * Get current session (returns null if not logged in)
   */
  async getSession() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) {
      debugLog.error("authService.getSession", error);
      return null;
    }
    return session;
  },

  /**
   * Get current user (returns null if not logged in)
   */
  async getCurrentUser() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const profile = await this.getProfile(user.id);
    return { user, profile };
  },

  /**
   * Get a user's profile from the profiles table
   */
  async getProfile(userId) {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      debugLog.warn("authService.getProfile", "Could not fetch profile", error);
      return null;
    }
    return data;
  },

  /**
   * Listen for auth state changes (login/logout)
   * Returns an unsubscribe function
   */
  onAuthStateChange(callback) {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      debugLog.action("Auth state changed", { event });
      callback(event, session);
    });
    return () => subscription.unsubscribe();
  },

  /**
   * Create a new user (admin only — for adding board members)
   */
  async createUser(email, password, fullName, role = "director") {
    // This uses the admin API — requires service role key
    // For now, users are created through Supabase dashboard
    // Profile is auto-created by the trigger
    debugLog.warn(
      "authService.createUser",
      "Use Supabase dashboard to create users"
    );
    throw new Error("User creation is managed through the Supabase dashboard");
  },
};
